import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyUserLanguage } from '../i18n';
import type {
  WaTourContentData,
  WaTourContentResponse,
  WaTourFinishResponse,
} from '../types/waBriefing';
import FeatureTour from './feature-tour/FeatureTour';
import type { TourTopic } from './feature-tour/tourScenes';
import PublicNoticePage from './common/PublicNoticePage';
import './wa-briefing-page.scss';

type PageState = 'loading' | 'ready' | 'done' | 'expired' | 'invalid' | 'error';

/**
 * The feature tour on its own page (/wa-tour?token=), for people who did not
 * open the Morning List that carried it. The last card is a summary of what
 * was covered; Back to WhatsApp hands the queued topics to the assistant.
 */
const WaTourPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [data, setData] = useState<WaTourContentData | null>(null);
  const [whatsappUrl, setWhatsappUrl] = useState<string | null>(null);

  const post = useCallback(
    async <T,>(path: string, body: Record<string, unknown>) => {
      const response = await fetch(`${apiBase}/wa-tour/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const payload: (T & { error?: string }) | null = await response
        .json()
        .catch(() => null);
      return { response, payload };
    },
    [apiBase, token],
  );

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${apiBase}/wa-tour/content?token=${encodeURIComponent(token)}`,
        );
        const payload: WaTourContentResponse | null = await response
          .json()
          .catch(() => null);
        if (cancelled) return;
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          setPageState(response.status === 404 ? 'invalid' : 'error');
          return;
        }
        applyUserLanguage(payload.data.language);
        setData(payload.data);
        setWhatsappUrl(payload.data.whatsappUrl);
        setPageState(payload.data.tour.finished ? 'done' : 'ready');
      } catch {
        if (!cancelled) setPageState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  const queue = useCallback(
    async (topics: TourTopic[]) => {
      try {
        await post('topics', { topics });
      } catch {
        // Kept on screen; the finish sends the full set again.
      }
    },
    [post],
  );

  const done = useCallback(
    async (topics: TourTopic[]) => {
      try {
        await post('topics', { topics });
        const { response, payload } = await post<WaTourFinishResponse>(
          'finish',
          {},
        );
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (payload?.data?.whatsappUrl)
          setWhatsappUrl(payload.data.whatsappUrl);
      } catch {
        // The summary card stays up; Back to WhatsApp finishes again.
      }
    },
    [post],
  );

  const back = useCallback(async () => {
    try {
      const { payload } = await post<WaTourFinishResponse>('handoff', {});
      const url = payload?.data?.whatsappUrl ?? whatsappUrl;
      if (url) window.location.assign(url);
    } catch {
      // Fall through: the notice below still links to WhatsApp.
    }
    setPageState('done');
  }, [post, whatsappUrl]);

  if (pageState === 'loading') return <PublicNoticePage loading />;

  if (pageState === 'done') {
    return (
      <PublicNoticePage
        title={t('tour.doneTitle')}
        body={t('tour.doneBody')}
        actions={
          whatsappUrl ? (
            <a className='public-notice__button' href={whatsappUrl}>
              {t('tour.openWhatsApp')}
            </a>
          ) : undefined
        }
      />
    );
  }

  if (pageState !== 'ready' || !data) {
    return (
      <PublicNoticePage
        title={
          pageState === 'expired'
            ? t('tour.linkExpiredTitle')
            : t('tour.somethingOffTitle')
        }
        body={
          pageState === 'expired'
            ? t('tour.expiredBody')
            : pageState === 'invalid'
              ? t('tour.invalidBody')
              : t('tour.errorBody')
        }
      />
    );
  }

  return (
    <div className='wa-briefing wa-briefing--tour' data-testid='wa-tour'>
      <FeatureTour
        ending={data.tour.ending}
        assistantName={data.assistantName}
        queued={data.tour.topics.map((topic) => topic.id)}
        context='standalone'
        onQueue={(topics) => void queue(topics)}
        onDone={(topics) => void done(topics)}
        onBack={() => void back()}
      />
    </div>
  );
};

export default WaTourPage;
